import { CopyAll } from '@mui/icons-material'
import { Box, CircularProgress, IconButton } from '@mui/material'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { clientsAPI } from 'api/clientsAPI'
import Input from 'components/RouteWizard/Input'
import { Select } from 'components/RouteWizard/Select'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'

const BOOKING_REFERENCE_VALUES_PAGE_SIZE = 100

const getPagedBookingReferenceValues = (payload: any) => {
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.value)) return payload.value
  if (Array.isArray(payload?.values)) return payload.values
  if (Array.isArray(payload?.data?.items)) return payload.data.items
  if (Array.isArray(payload?.data?.value)) return payload.data.value
  return []
}

const getPagedBookingReferenceHasMore = (
  payload: any,
  page: number,
  limit: number,
  resultCount: number,
) => {
  const explicitHasMore =
    payload?.hasMore ??
    payload?.pagination?.hasMore ??
    payload?.meta?.hasMore ??
    payload?.pageInfo?.hasMore

  if (typeof explicitHasMore === 'boolean') {
    return explicitHasMore
  }

  const total =
    payload?.totalSize ??
    payload?.totalCount ??
    payload?.total ??
    payload?.count ??
    payload?.pagination?.totalSize ??
    payload?.pagination?.totalCount ??
    payload?.pagination?.total ??
    payload?.meta?.totalSize ??
    payload?.meta?.totalCount

  if (typeof total === 'number') {
    return page * limit < total
  }

  return resultCount >= limit
}

type BookingReferenceOption = {
  label: string
  value: string
}

const BookingRefsForm = ({
  editStates,
  preparedQuote,
  currentBooking,
  updatePreparedAmend,
  updatePreparedQuote,
}: any) => {
  const formMethods = useForm<Record<string, string>>({
    defaultValues: {},
  })
  const isSyncingFormRef = useRef(false)

  // Normalize strings by replacing en-dashes and em-dashes with regular hyphens
  const normalizeString = (str: string) => {
    return str.replace(/–|—/g, '-')
  }

  const bookingClientId =
    currentBooking?.booking?.clientId ??
    currentBooking?.booking?.client?.clientId ??
    currentBooking?.clientId ??
    currentBooking?.client?.clientId

  // Load booking reference values with pagination and search
  const loadValuesOptions = useCallback(
    async (
      bookingReferenceId: number,
      inputValue: string,
      page: number,
      limit: number,
    ) => {
      console.log(
        'BookingRefsForm: Loading values for booking ref',
        bookingReferenceId,
        'client ID:',
        bookingClientId,
      )
      try {
        if (!bookingClientId) {
          console.warn(
            'BookingRefsForm: Missing client ID for booking reference values',
          )
          return { options: [], hasMore: false }
        }
        const offset = (page - 1) * limit
        const response = await clientsAPI.getBookingReferenceValues(
          bookingClientId,
          bookingReferenceId,
          {
            limit,
            offset,
            search: inputValue || undefined,
          },
        )
        console.log('BookingRefsForm: API Response:', response?.data)
        const values = getPagedBookingReferenceValues(response?.data)
        console.log('BookingRefsForm: Parsed values count:', values.length)
        const options = values
          .map((val: any) => {
            const stringVal = typeof val === 'string' ? val : val.value || val
            if (stringVal === null || stringVal === undefined) return null
            const normalizedVal = normalizeString(String(stringVal))
            return {
              label: normalizedVal,
              value: normalizedVal,
            }
          })
          .filter(Boolean) as BookingReferenceOption[]
        console.log('BookingRefsForm: Returning options count:', options.length)
        return {
          options,
          hasMore: getPagedBookingReferenceHasMore(
            response?.data,
            page,
            limit,
            values.length,
          ),
        }
      } catch (error) {
        console.error('BookingRefsForm: Failed to load values:', error)
        // Fallback: use local values from the booking reference
        const bookingRef = currentBooking.accountRefs.find(
          (ref: any) => ref.id === bookingReferenceId,
        )
        console.log('BookingRefsForm: Using fallback, found ref:', bookingRef)
        if (bookingRef && bookingRef.values) {
          const filteredValues = (
            Array.isArray(bookingRef.values) ? bookingRef.values : []
          )
            .map((val: any) => {
              const stringVal = typeof val === 'string' ? val : val.value || val
              const normalizedVal = normalizeString(String(stringVal))
              return { label: normalizedVal, value: normalizedVal }
            })
            .filter((opt: any) =>
              inputValue
                ? opt.label.toLowerCase().includes(inputValue.toLowerCase())
                : true,
            )

          const start = (page - 1) * limit
          const pagedOptions = filteredValues.slice(start, start + limit)

          console.log(
            'BookingRefsForm: Fallback options count:',
            pagedOptions.length,
          )
          return {
            options: pagedOptions,
            hasMore: start + limit < filteredValues.length,
          }
        }
        console.log('BookingRefsForm: No fallback options available')
        return { options: [], hasMore: false }
      }
    },
    [bookingClientId, currentBooking.accountRefs],
  )

  const updateBookingRefs = (change: any, bookingReferenceId: number) => {
    const updatedQuoteObj = [...preparedQuote.bookingReferences]
    const amendObj = [...preparedQuote.bookingReferences]

    const existingQuoteIndex = updatedQuoteObj.findIndex(
      (ref: any) => ref.bookingReferenceId === bookingReferenceId,
    )
    const existingAmendIndex = amendObj.findIndex(
      (ref: any) => ref.bookingReferenceId === bookingReferenceId,
    )

    if (existingQuoteIndex === -1) {
      updatedQuoteObj.push({ bookingReferenceId, value: change })
    } else {
      updatedQuoteObj[existingQuoteIndex].value = change
    }

    if (existingAmendIndex === -1) {
      amendObj.push({ bookingReferenceId, value: change })
    } else {
      amendObj[existingAmendIndex].value = change
    }

    const strippedAmendObj = amendObj
      .filter((br) => br.value)
      .map((br) => ({
        bookingReferenceId: br.bookingReferenceId,
        value: br.value,
      }))

    updatePreparedQuote('bookingReferences', updatedQuoteObj)
    updatePreparedAmend('bookingReferences', strippedAmendObj)
  }

  const bookingRefs = preparedQuote?.bookingReferences ?? []

  const mergedArray = useMemo(() => {
    const valueMap = new Map(
      bookingRefs.map((item: any) => [item.bookingReferenceId, item.value]),
    )
    return currentBooking.accountRefs.map((obj: any) => ({
      ...obj,
      value: valueMap.get(obj.id) ?? null,
    }))
  }, [bookingRefs, currentBooking.accountRefs])

  const mergedArrayWithValues = useMemo(
    () => mergedArray.filter((ar: any) => !!ar.value),
    [mergedArray],
  )

  const getFieldName = useCallback((id: number) => `bookingRef_${id}`, [])

  useEffect(() => {
    if (!editStates.bookingRefs) return

    isSyncingFormRef.current = true
    mergedArray.forEach((ar: any) => {
      const fieldName = getFieldName(ar.id)
      const nextValue = ar.value ?? ''
      if (formMethods.getValues(fieldName) !== nextValue) {
        formMethods.setValue(fieldName, nextValue, {
          shouldDirty: false,
          shouldTouch: false,
        })
      }
    })

    const timeoutId = window.setTimeout(() => {
      isSyncingFormRef.current = false
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [editStates.bookingRefs, formMethods, getFieldName, mergedArray])

  useEffect(() => {
    if (!editStates.bookingRefs) return

    const subscription = formMethods.watch((values, info) => {
      if (isSyncingFormRef.current) return

      const fieldName = info.name
      if (!fieldName?.startsWith('bookingRef_')) return

      const bookingReferenceId = Number(fieldName.replace('bookingRef_', ''))
      if (!Number.isFinite(bookingReferenceId)) return

      updateBookingRefs(values[fieldName] ?? '', bookingReferenceId)
    })

    return () => subscription.unsubscribe()
  }, [editStates.bookingRefs, formMethods, updateBookingRefs])

  return editStates.bookingRefs ? (
    preparedQuote ? (
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 300 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>References</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mergedArray.map((ar: any) => {
                return (
                  <TableRow
                    key={`${ar.title}${ar.id}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {ar.title}
                    </TableCell>
                    <TableCell align="right" sx={{ paddingY: '2px' }}>
                      <Box sx={{ minWidth: 220 }}>
                        {ar.referenceType === 'Array' ? (
                          <Select
                            control={formMethods.control}
                            name={getFieldName(ar.id)}
                            label={ar.title}
                            size="small"
                            searchable
                            clearable
                            pageSize={BOOKING_REFERENCE_VALUES_PAGE_SIZE}
                            defaultValue={ar.value ?? ''}
                            loadOptions={async ({ search, page, limit }) =>
                              loadValuesOptions(ar.id, search, page, limit)
                            }
                          />
                        ) : (
                          <Input
                            control={formMethods.control}
                            name={getFieldName(ar.id)}
                            label={ar.title}
                            size="small"
                            autoComplete="off"
                            defaultValue={ar.value ?? ''}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    ) : (
      <CircularProgress />
    )
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 300 }} size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell align="right">
                Copy All
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(
                      mergedArray
                        .map((ar) => {
                          if (ar.value) return `${ar.title}: ${ar.value}`
                        })
                        .join('\n'),
                    )
                  }}
                  size="small"
                  sx={{ marginLeft: '5px' }}
                >
                  <CopyAll sx={{ fontSize: 12 }} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergedArray.map((ar: any) => {
              if (ar.value)
                return (
                  <TableRow
                    key={`${ar.title}${ar.id}`}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ padding: 0, paddingLeft: '16px', fontSize: '11px' }}
                    >
                      {ar.title}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ padding: 0, fontSize: '11px' }}
                    >
                      {ar.value}
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `${ar.title}: ${ar.value}`,
                          )
                        }}
                        size="small"
                        sx={{ marginLeft: '5px' }}
                      >
                        <CopyAll sx={{ fontSize: 12 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
            })}
            {mergedArrayWithValues.length > 0 ? (
              <></>
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {`None applied`}
                </TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default memo(BookingRefsForm)
